import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PartialTable from 'components/partials/PartialTable';
import { Link } from 'react-router-dom';
import Flex from 'components/common/Flex';
import { splitStringMapValueToDropDown } from 'helpers/dropdownFuncHelper';
import dayjs from 'dayjs';
import { priorites, progresses } from 'data/TempData';
import { getDepartmentOptionList } from 'services/customer-service/scActionPlanService';
import UsersInCellWidget from 'components/common/users/UsersInCellWidget';
import SoftBadge from 'components/common/SoftBadge';
import classNames from 'classnames';
import Priority from 'components/pages/customer-service/action-plans/action-plan-dashboard/Priority';
import { truncateHtmlText } from 'helpers/utils';
import {
  getSetting,
  KEY_COL_SC_ACTION_PLAN
} from 'services/base-services/userSettingServices';

const CustomerFocusTableColumns = async colSort => {
  const dataDepartments = (await getDepartmentOptionList()).map(x => {
    return {
      ...x,
      value: x.id,
      label: x.name
    };
  });

  const response = await getSetting(KEY_COL_SC_ACTION_PLAN);
 
  let columns = [];
  if (response && response.success && response.data) {
    columns = JSON.parse(response.data.settingValue);
  }

  return [
    {
      accessor: 'id',
      Header: 'ID',
      isVisible: false
    },
    {
      accessor: 'meetingId',
      Header: 'MeetingId',
      isVisible: false
    },
    {
      accessor: 'description',
      Header: 'description',
      isVisible: false
    },
    {
      desc: false,
      accessor: 'title',
      Header: 'Title',

      Cell: rowData => {
        const { title, id, meetingId, description } = rowData.row.original;
        return (
          <Link
            data-tooltip-id="my-tooltip"
            data-tooltip-place="right-start"
            data-tooltip-html={truncateHtmlText(description, 200, 2000)}
            to={
              `/customer-service/meetings/${meetingId}/action-plan-manager/` +
              id
            }
          >
            <Flex alignItems="center">
              <div className="flex-1">
                <h5 style={{ color: '#0052CC' }} className="mb-0 fs--1">
                  {title}
                </h5>
              </div>
            </Flex>
          </Link>
        );
      }
    },
    {
      accessor: 'meetingTitle',
      Header: 'Meeting',
      Cell: rowData => {
        const { meetingTitle, meetingId } = rowData.row.original;
        return (
          <Link to={`/customer-service/meetings/${meetingId}`}>
            <Flex alignItems="center">
              <div className="flex-1">
                <h5 style={{ color: '#0052CC' }} className="mb-0 fs--1">
                  {meetingTitle}
                </h5>
              </div>
            </Flex>
          </Link>
        );
      }
    },
    {
      accessor: 'targetDate',
      Header: 'Target Date',
      Cell: rowData => {
        const { targetDate } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <div className="flex-1">
              <h5 className="mb-0 fs--1">
                {targetDate && dayjs(targetDate).format('DD/MM/YYYY')}
              </h5>
            </div>
          </Flex>
        );
      }
    },
    {
      accessor: 'assignees',
      Header: 'Responsible persons',
      Cell: rowData => {
        const { assignees } = rowData.row.original;
        return <UsersInCellWidget usersText={assignees} />;
      }
    },
    {
      accessor: 'requestParticipants',
      Header: 'Request participants',
      headerProps: { style: { minWidth: '10px' }, className: 'ps-5' },
      cellProps: { className: 'ps-5' },
      Cell: rowData => {
        const { requestParticipants } = rowData.row.original;
        return <UsersInCellWidget usersText={requestParticipants} />;
      }
    },
    {
      accessor: 'reporter',
      Header: 'Reporter',
      Cell: rowData => {
        const { reporter } = rowData.row.original;
        return <UsersInCellWidget showName usersText={reporter?.toString()} />;
      }
    },
    {
      accessor: 'departments',
      Header: 'Departments',
      Cell: rowData => {
        const { departments } = rowData.row.original;
        const data = splitStringMapValueToDropDown(
          departments,
          dataDepartments
        );
        return (
          <>
            {data.map(i => (
              <Link to="#" key={i.value}>
                <Flex alignItems="center">
                  <div className="flex-1">
                    <h5 className="mb-0 fs--1">{i.label}</h5>
                  </div>
                </Flex>
              </Link>
            ))}
          </>
        );
      }
    },
    {
      accessor: 'progress',
      Header: 'Status',
      Cell: rowData => {
        const { progress } = rowData.row.original;
        const data = splitStringMapValueToDropDown(
          progress?.toString(),
          progresses
        );
        return (
          <>
            {data.map(i => (
              <SoftBadge
                key={i.value}
                bg={classNames({
                  success: i.label === 'Completed',
                  warning: i.label === 'Inprogress',
                  danger: i.label === 'To Do'
                })}
                className="fs--1 w-100"
              >
                {i.label}
              </SoftBadge>
            ))}
          </>
        );
      }
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      Cell: rowData => {
        const { priority } = rowData.row.original;
        const data = splitStringMapValueToDropDown(
          priority?.toString(),
          priorites
        );
        return (
          <>
            {data.map(i => (
              <Priority key={i.value} param={i} />
            ))}
          </>
        );
      }
    }
  ].map(i => {
    const col = columns.find(x => x.accessor === i.accessor);
    if (colSort && colSort.sortBy == i.accessor) {
      return {
        ...i,
        isVisible: col ? col.isVisible : i.isVisible,
        isBeSortedDesc: colSort.isBeSortedDesc,
        isBeSorted: colSort.isBeSorted
      };
    }
    return {
      ...i,
      isVisible: col ? col.isVisible : i.isVisible
    };
  });
};

const CustomersFocusTable = ({
  reload,
  data,
  paging,
  total,
  nextPage,
  previousPage,
  gotoPage,
  onPageSizeChange,
  colSort,
  handleSort
}) => {
  const [columns, setColumns] = useState([]);
  useEffect(() => {
    const med = async () => {
      const initialCols = await CustomerFocusTableColumns(colSort);
      setColumns(initialCols);
    };

    med();
  }, [reload, colSort]);

  return (
    <>
      <PartialTable
        data={data}
        columns={columns}
        paging={paging}
        total={total}
        nextPage={nextPage}
        previousPage={previousPage}
        gotoPage={gotoPage}
        onPageSizeChange={onPageSizeChange}
        textAlign=""
        beSortOption={{ canSort: true, handleSort: handleSort }}
      />
    </>
  );
};

CustomersFocusTable.propTypes = {
  reload: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object),
  paging: PropTypes.object,
  total: PropTypes.number,
  nextPage: PropTypes.func,
  previousPage: PropTypes.func,
  gotoPage: PropTypes.func,
  handleSort: PropTypes.func,
  colSort: PropTypes.object,
  onPageSizeChange: PropTypes.func
};

export { CustomersFocusTable, CustomerFocusTableColumns };
